/**
 * 焦点虚化（P4c，design §11.11）的纯逻辑：三种状态各给多大强度、强度怎么淡入淡出、
 * 清楚圈的世界半径怎么换算成屏幕比例。不碰 three.js，好写单元测试；每帧由 galaxy.ts 调用。
 * 焦点用镜头的注视点：看照片时它在选中的星上、停站时在当天的锚点上、轮转时在光环正面附近；
 * 换星、翻页、离站时注视点跟着镜头飞行平滑地挪过去，清楚的那块不会突然跳到别处。
 */
import { SPIRAL } from '@star-sea/shared';

// ── 强度和节奏（浏览器里看效果时调这里）──
/** 看照片 */
export const FOCUS_PHOTO = 0.75;
/** 漫游停站（暂停在站上也算） */
export const FOCUS_STOP = 0.6;
/** 轮转光环展开着 */
export const FOCUS_RING = 0.4;
/** 淡入、淡出的时长（秒）：目标一换就从当时的显示值重新走一整段 */
export const FADE_IN = 0.7;
export const FADE_OUT = 0.9;

// ── 清楚圈（浏览器里看效果时调这里）──
/** 星团半径之外的余量（世界单位）：一天的星铺在 0.17·√n 里，余量把边上零散的星和选中星的圈也罩进来 */
export const CLUSTER_PAD = 0.6;
/** 内圈（完全清楚的范围）是屏幕高度的比例，夹在这个区间 */
export const INNER_MIN = 0.1;
export const INNER_MAX = 0.4;
/** 外圈 = 内圈 + 这个值：从内圈边缘到外圈边缘 smoothstep 过渡到完全化开 */
export const OUTER_GAP = 0.35;
/** 轮转：光环占大半个屏幕、卡片又画在叠加层不参与模糊，内圈直接用固定的屏幕比例 */
export const RING_INNER = 0.3;

/** 这一天还有几张没删的 → 星团半径（世界单位）：向日葵铺开 0.17·√n，再加余量 */
export function clusterRadius(live: number): number {
  return SPIRAL.cluster * Math.sqrt(Math.max(1, live)) + CLUSTER_PAD;
}

/** 世界半径 → 屏幕高度的比例：r / (2·d·tan(fov/2))，d 是镜头到注视点的距离；再夹到内圈范围 */
export function innerRadius(worldRadius: number, distance: number, fovDeg: number): number {
  const k = worldRadius / (2 * Math.max(1e-3, distance) * Math.tan((fovDeg * Math.PI) / 360));
  return Math.min(INNER_MAX, Math.max(INNER_MIN, k));
}

export function outerRadius(inner: number): number {
  return inner + OUTER_GAP;
}

/** 三种状态不会同时出现；万一撞上按「照片 > 停站 > 轮转」取。allowed 是辉光开着、不在入场、没伪装 */
export function focusGoal(state: { photo: boolean; stop: boolean; ring: boolean; allowed: boolean }): number {
  if (!state.allowed) return 0;
  if (state.photo) return FOCUS_PHOTO;
  if (state.stop) return FOCUS_STOP;
  if (state.ring) return FOCUS_RING;
  return 0;
}

/** 淡入淡出用的过渡：from → to 走 dur 秒（往上 FADE_IN、往下 FADE_OUT），t 是已经走的秒数 */
export interface FocusEase {
  from: number;
  to: number;
  t: number;
  dur: number;
}

export function focusEase(): FocusEase {
  return { from: 0, to: 0, t: 0, dur: 0 };
}

const smooth = (k: number) => k * k * (3 - 2 * k);

/** 当前显示出来的强度：进度上套一层 smoothstep，两端不生硬 */
export function focusValue(e: FocusEase): number {
  if (e.dur <= 0) return e.to;
  return e.from + (e.to - e.from) * smooth(Math.min(1, e.t / e.dur));
}

/**
 * 每帧一步：目标变了就从当前的显示值重新起一段（不是从 0、也不是接着原来的速率），
 * 显示值本身连续，换目标不跳
 */
export function focusStep(e: FocusEase, goal: number, dt: number): number {
  if (goal !== e.to) {
    e.from = focusValue(e);
    e.to = goal;
    e.t = 0;
    e.dur = goal > e.from ? FADE_IN : FADE_OUT;
    if (e.from === goal) e.dur = 0;
  }
  e.t = Math.min(e.dur, e.t + dt);
  return focusValue(e);
}
